import type { PlatformProductSubscription } from "@/api/orval/model";
import { getAllowAnonProductSubscriptionsForPlatform } from "@/api/orval/platforms/platforms";
import { getProgress, getWaitlistEntry } from "@/api/orval/ageless-onboarding/ageless-onboarding";

const DEFAULT: PlatformProductSubscription[] = ["RepeatMD"];

const HOST_RULES: {
  host: RegExp;
  subscriptions: PlatformProductSubscription[];
}[] = [
  { host: /ageless\.ai$/i, subscriptions: ["AgelessAi"] },
  { host: /agelessai\.dev$/i, subscriptions: ["AgelessAi"] },
  { host: /agelessleads\.com$/i, subscriptions: ["AgelessAi"] },
  { host: /repeatmd\./i, subscriptions: ["RepeatMD"] },
  { host: /^localhost$/i, subscriptions: ["RepeatMD", "AgelessAi"] },
];

const getSubscriptionsFromHost = (hostname?: string): PlatformProductSubscription[] => {
  if (!hostname) {
    return DEFAULT;
  }

  const match = HOST_RULES.find((rule) => rule.host.test(hostname));
  return match?.subscriptions ?? DEFAULT;
};

export const getSubscriptionsFromWindowHost = (): PlatformProductSubscription[] => {
  if (typeof globalThis === "undefined") {
    return DEFAULT;
  }

  return getSubscriptionsFromHost(globalThis.location.hostname);
};

export const resolveSubscriptionsForPlatform = async ({
  platformId,
  request,
}: {
  platformId?: string;
  request: Request;
}): Promise<PlatformProductSubscription[]> => {
  if (platformId) {
    const response = await getAllowAnonProductSubscriptionsForPlatform(platformId);
    return response.products ?? [];
  }

  return getSubscriptionsFromHost(new URL(request.url).hostname);
};

/**
 * Loader guard for ageless routes. Checks that the platform has AgelessAi
 * and that onboarding is complete. Returns null if access is allowed,
 * or a redirect path if the user should be sent elsewhere.
 *
 * Loaders that also need the subscription list for their own logic should fetch it once
 * via `resolveSubscriptionsForPlatform` and pass it in, so the guard doesn't repeat the
 * same uncached GET.
 */
export const guardAgelessRoute = async (
  platformId: string,
  request: Request,
  prefetchedSubscriptions?: PlatformProductSubscription[],
): Promise<string | null> => {
  const subscriptions =
    prefetchedSubscriptions ??
    (await resolveSubscriptionsForPlatform({
      platformId,
      request,
    }));

  if (!subscriptions.includes("AgelessAi")) {
    return "/admin";
  }

  // Beta platforms are allowed to bypass onboarding and waitlist since
  // they had everything enabled before those were implemented
  if (BETA_PLATFORM_IDS.has(platformId)) {
    return null;
  }

  const progress = await getProgress(platformId);

  if (!progress?.isComplete) {
    return `/admin/platforms/${platformId}/ageless/onboarding`;
  }

  try {
    const waitlist = await getWaitlistEntry(platformId);

    if (!waitlist) {
      return null;
    }

    if (!waitlist.isAdmitted) {
      return `/admin/platforms/${platformId}/ageless/waitlist`;
    }
  } catch {
    return null;
  }

  return null;
};

export const BETA_PLATFORM_IDS = new Set([
  "fa5fe199-f717-4fe6-99b7-5b42f2a84b1e",
  "019c85cf-d4e3-7946-8b1b-d143f20c721a",
  "a3cd1da3-3822-490a-a253-036331e9413f",
  "bc6c60e1-1655-4f86-8d3b-6457d5520aff",
  "c5201ee3-d51c-426c-a8e9-4a34e40167d0",
  "bc6c60e1-1655-4f86-8d3b-6457d5520adf",
  "e620ed54-49c7-4548-ba6f-f664a16d1e5b",
  "58c59747-23b3-4727-a831-3bda193b8334",
  "d8f81a3c-b8f5-437c-ab10-054d4c1dfef8",
  "019c920b-2675-7b84-b559-7a17ca06eaed",
  "30d2dbaa-e889-4b93-861a-9b7a2cd428d2",
  "237af1b1-bdd8-4b86-814a-907e3facb5f7",
  "c0aecefa-ce71-42eb-a047-56a6323259a3",
  "363d2d34-ab28-4887-aa0d-bbf3826306c3",
  "f6d4690d-4432-440b-a9f6-9a3e5922523f",
  "cd3fbd58-2800-40f2-8a57-f1597b8de496",
  "ce40dff8-58d8-4600-a8b2-9365e8914198",
  "cfb29b4d-c36e-4f4f-a69c-2b2d390aec6d",
  "d6a76503-1427-4070-bbc5-140afd9bac0a",
  "a0238386-2af6-4a52-8aad-6d88238684fb",
  "3b84dff6-5bf5-4621-86f9-62ecba269ba3",
  "019a4aad-5687-7653-bba7-9192b6a8fef5",
  "0bec856e-cb0d-4e89-a7f0-c8b286a76993",
]);
